import { getProducts } from '@/utils/apis/order-service';
import { useAuthStore } from '@/utils/stores/useAuthStore';
import { useUserStore } from '@/utils/stores/useUserStore';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface UseSortingButtonProps {
    selectedItems: { [key: number]: { isToggled: boolean; giftEmail?: string; giftSaved?: boolean } };
    sortingItems: any[] | null;
    handleConfirm: () => Promise<void>;
}

interface UseSortingButtonReturn {
    isLoading: boolean;
    hasProducts: boolean;
    buttonVariant: 'default' | 'secondary';
    isButtonDisabled: boolean;
    buttonText: string;
    handleProceed: () => Promise<void>;
}

export const useSortingButton = ({ selectedItems, sortingItems, handleConfirm }: UseSortingButtonProps): UseSortingButtonReturn => {
    const [isLoading, setIsLoading] = useState(false);
    const [hasProducts, setHasProducts] = useState(false);
    const navigate = useNavigate();
    const { auth } = useAuthStore();
    const { userData } = useUserStore();

    const checkSavedPurchases = async () => {
        try {
            const products = await getProducts(auth.userId!, auth.idToken!);
            setHasProducts(Boolean(products && products.length > 0));
            if (products && products.length === 0) {
                navigate('/fetch-data');
            }
        } catch (error) {
            console.error('Error checking saved purchases:', error);
        }
    };

    useEffect(() => {
        checkSavedPurchases();
    }, [checkSavedPurchases]);

    const hasSelectedItems = Object.values(selectedItems).some(item => item.isToggled);
    const hasGiftEmails = Object.values(selectedItems).some(item => 
        item.giftEmail && 
        item.giftEmail.trim() !== '' && 
        item.giftEmail !== userData.email &&
        item.giftSaved // Only consider saved gifts
    );
    const hasAnySelection = hasSelectedItems || hasGiftEmails;
    const buttonVariant = hasProducts && !hasAnySelection ? 'secondary' : 'default';
    const isButtonDisabled = !hasProducts && !hasAnySelection;
    const buttonText = hasProducts && !hasAnySelection ? 'Continua senza assegnare' : 'Conferma';

    const handleProceed = async () => {
        try {
            setIsLoading(true);

            // If button is secondary variant (has products but no selections), navigate to fetch-data
            if (buttonVariant === 'secondary') {
                navigate('/fetch-data');
                return;
            }

            // For default variant (has selections), call handleConfirm from useSortingItems
            await handleConfirm();
        } catch (error) {
            console.error('Error in handleProceed:', error);
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    return {
        isLoading,
        hasProducts,
        buttonVariant,
        isButtonDisabled,
        buttonText,
        handleProceed
    };
}; 